/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { DialogButton, Type } from "../../models/dialog-button";
import {Component, OnInit} from '@angular/core';
import {DialogInstance} from "../../models/dialog-instance";
import {DialogService} from "../../services/dialog.service";
import {DialogWebSocketService} from "../../services/dialog-web-socket.service";
import {DialogData} from '../../models/dialog-data';
import {ViewChild} from "@angular/core";
import {MapComponent} from "../map/map.component";
import {DialogItem} from "../../models/dialog-item";
import {CanActivateAuthGuard} from "../../can-activate.authguard";
import {ZoneInstance} from "../../models/zone-instance";
import { ImageService } from "app/services/image.service";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "../simple-success-error-dialog-box/simple-success-error-dialog-box.component";

declare var $: any;

const ID = "dialogAlertModal";

@Component({
  selector: 'app-alert-dialog',
  templateUrl: './alert-dialog.component.html',
  styles: [
    '.alert-disabled { pointer-events: none; }'
  ]
})
export class AlertDialogComponent implements OnInit {
  @ViewChild('alertMapComponent') mapComponent: MapComponent;

    /** Reference to the SimpleSuccessErrorDialog used to display error */
    @ViewChild('successErrorDialog') 
    simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  overrideDefaults: boolean;
  displayAssets: boolean;
  displayPeople: boolean;
  displayPerimeters: boolean;
  displayZones: boolean;
  displaySublocations: boolean;
  showZoneNames: boolean;
  showSublocationNames: boolean;
  showBreadcrumbs: boolean;
  zoomToZone: ZoneInstance;
  assetsVIP = [];
  peopleVIP = [];
  mapVIPs = [];

  //ID of the Alert Dialog <div>, used by JQuery below to show/hide the Dialog.  Stored as a const to reduce the number of places it is specified.
  mId = ID;

  //Dialog Instance which is currently being displayed
  mDialogInstance: DialogInstance = null;

  //Boolean Flag used to indicate if the Dialog is waiting for another DialogInstance to come in to be displayed.
  mWaitForNextDialogFlag = false;

  constructor(
    private mDialogService: DialogService,
    private mDialogWebSocketService: DialogWebSocketService,
    private mImageService: ImageService
  ) { }


  ngOnInit(): void {
    //Subscribe to the Dialog Events via the Web Socket which should be displayed...
    this.mDialogWebSocketService.getDialogInstanceEventListeners().subscribe(
      newDialog => {
        console.log("Received Dialog Instance via WebSocket...");

        //Dialog Instances coming in via the Web Socket are new and won't have their data created... Create the data based on the Dialog Item's, defaulting them to FALSE so they are not checked...
        newDialog.data = [];
        if (newDialog.dialog.items != null) {
          newDialog.dialog.items.forEach(item => {
            const datum = new DialogData();
            datum.dialogItem = item;
            datum.checked = false;
            newDialog.data.push(datum);
          });
        }

        //Reorder the DialogData based on the DialogItem's order field
        this.sortDialogDataByDialogItemOrder(newDialog);

        //Show the Dialog Instance
        this.showDialogInstance(newDialog);
      }
    );

    //Subscribe to the Dialogs selected/clicked on by the User which should be displayed...
    this.mDialogWebSocketService.getDialogInstanceOnDemandEventListeners().subscribe(
      newDialog => {
        console.log("Received Dialog Instance User Selected... ID: " + newDialog.id + ", Data: " + newDialog.data);

        //Dialog Instances that are being selected by the User might not have been retrieved property (No Data if not retrieved by ID), retrieve the Dialog Instance using the ID to fetch the Data
        this.mDialogService
          .getDialogInstanceById(newDialog.id)
          .then(
            retDialog => {
              console.log("Retrieved Dialog Instance: ID: " + retDialog.id + ", Data: " + retDialog.data);

              //Old Dialogs might not have Data for all the Items for the checklist (Old UI only sent what was checked), go through and find any that don't have Data created for them and create the data
              const itemsToAdd: DialogItem[] = [];
              //Figure out what DialogItem's need their DialogData created...
              if (retDialog.dialog.items != null) {
                retDialog.dialog.items.forEach(item => {
                  const datum = retDialog.data == null? null : retDialog.data.find(d => d.dialogItem.id == item.id);
                  if (datum == null) {
                    itemsToAdd.push(item);
                  }
                });
              }
              //Create the DialogData for any DialogItem that wasn't specified...
              itemsToAdd.forEach(item => {
                const datum = new DialogData();
                datum.dialogItem = item;
                datum.checked = false;
                retDialog.data.push(datum);
              });

              //Reorder the DialogData based on the DialogItem's order field
              this.sortDialogDataByDialogItemOrder(retDialog);

              //Show the Dialog Instance
              this.showDialogInstance(retDialog);
            },
            error => this.handleError(error)
          )

      }
    );
  }

  private sortDialogDataByDialogItemOrder(dialogInstance: DialogInstance): void {
    if (dialogInstance != null && dialogInstance.data != null) {
      dialogInstance.data.sort( function(a: DialogData, b: DialogData) {
        return a.dialogItem.order - b.dialogItem.order;
      });
    }
  }

  private showDialogInstance(dialogInstance: DialogInstance): void {
    CanActivateAuthGuard.popupActive = true;

    console.log("showDialog() - Dialog Instance: " + dialogInstance + " - Current Dialog Instance: " + this.mDialogInstance + " - Is Waiting? " + this.mWaitForNextDialogFlag);

    //If there is no Dialog Instance being shown, or it is waiting for the next one in a chain... Display the new Dialog Instance
    if (this.mDialogInstance == null || this.mWaitForNextDialogFlag) {
      this.setDialogInstance(dialogInstance);
    } else {
      //Ignore it since one is already displayed and it is not waiting for the next one in a chain...
      console.log("Ignoring Dialog Instance as one is already up and it is not waiting for the next one...");
    }
  }

  private setDialogInstance(dialogInstance: DialogInstance): void {
    console.log("setDialogInstance() - Dialog Instance: " + dialogInstance);
    this.mDialogInstance = dialogInstance;

    //New Dialog Instances being shown for the first time won't have their Data created for the Checklist... Create it defaulting everything to NOT be selected
    if (this.mDialogInstance != null && (this.mDialogInstance.data == null || this.mDialogInstance.data.length == 0)) {
      this.mDialogInstance.data = [];
      if (this.mDialogInstance.dialog.items != null) {
        this.mDialogInstance.dialog.items.forEach(item => {
          const datum = new DialogData();
          datum.dialogItem = item;
          datum.checked = false;
          this.mDialogInstance.data.push(datum);
        });
      }
    }

    // If the dialog overrides the defaults, make it so
    if (this.mDialogInstance && this.mDialogInstance.dialog && this.mDialogInstance.dialog.mapOptions) {
      console.log("Defaults override");
      this.overrideDefaults = true;
      this.displayAssets = this.mDialogInstance.dialog.mapOptions.assets;
      this.displayPeople = this.mDialogInstance.dialog.mapOptions.people;
      this.showBreadcrumbs = this.mDialogInstance.dialog.mapOptions.breadcrumbs;
      this.displayPerimeters = this.mDialogInstance.dialog.mapOptions.perimeters;
      this.displayZones = this.mDialogInstance.dialog.mapOptions.zones;
      this.showZoneNames = this.mDialogInstance.dialog.mapOptions.zonenames;
      this.displaySublocations = this.mDialogInstance.dialog.mapOptions.sublocations;
      this.showSublocationNames = this.mDialogInstance.dialog.mapOptions.sublocationnames;
      if (this.mDialogInstance.dialog.mapOptions.zoomToZone) {
        this.zoomToZone = this.mDialogInstance.zoneInstance;
      } else {
        this.zoomToZone = null;
      }
      this.mapVIPs = this.mDialogInstance.mapVips;

    } else {
      this.overrideDefaults = false;
      this.zoomToZone = null;
      this.mapVIPs = [];
    }


    this.show();
  }

  private show(): void {
    $("#" + this.mId).show();
  }

  hide(): void {
    CanActivateAuthGuard.popupActive = false;
    this.mDialogInstance = null;
    $("#" + this.mId).hide();
  }

  getPathForUserImage(): string {
    return this.mImageService.getImageSourceForUser(this.mDialogInstance.user);
  }

  getPathForSourceImage(): string {
    return this.mImageService.getImageSourceForInstance(this.mDialogInstance.source);
  }

  getClassForButton(button: DialogButton): string[] {
    const retVal = [ 'btn' ];
    if (button != null && button.color != null && button.color.red != null) {
      switch (button.color.red) {
        case 2: {
          retVal.push('btn-default');
          break;
        }
        case 3: {
          retVal.push('btn-primary');
          break;
        }
        case 4: {
          retVal.push('btn-success');
          break;
        }
        case 5: {
          retVal.push('btn-info');
          break;
        }
        case 6: {
          retVal.push('btn-warning');
          break;
        }
        case 7: {
          retVal.push('btn-danger');
          break;
        }
      }
    }
    return retVal;
  }

  async onButtonClick(button: DialogButton): Promise<void> {
    console.log("onButtonClick() - Button: " + (button == null? "*NULL*" : (button.id + "/" + button.code + "/" + button.type + "/" + button.label)));

    //Check to see if the Button Type is a wait Button, update the Flag...
    this.mWaitForNextDialogFlag = this.isButtonTypeWait(button.type);

    //Attempt to update the Dialog Instance, informing the backend which button was clicked...
    try {
      const savedDialogInstance = await this.mDialogService
                                          .updateDialogInstanceWithButton(this.mDialogInstance, button.id);
      console.log("Successfully updated Dialog Instance!");
      if (this.mWaitForNextDialogFlag) {
        console.log("Waiting for next Dialog Instance in chain...");
      } else {
        console.log("Closing Dialog...");
        CanActivateAuthGuard.popupActive = false;
        this.hide();
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  isButtonTypeWait(buttonType: Type): boolean {
    switch (buttonType) {
      case Type.ACCEPT_WAIT:
      case Type.DECLINE_WAIT:
      case Type.NONE_WAIT: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  handleError(error: any): void {
    console.error("handleError() - Error: " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }

}
